import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPricingRules,
  createRule,
  updateRule,
  deleteRule,
  overridePrice,
  fetchAuditLog,
  type CreateRulePayload,
  type UpdateRulePayload,
  type OverridePricePayload,
  type AuditLogQuery,
} from '@/services/api/pricing.api';

export function usePricingRules(unitId?: string) {
  return useQuery({
    queryKey: ['pricing', 'rules', unitId],
    queryFn: () => fetchPricingRules(unitId),
    staleTime: 30_000,
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRulePayload) => createRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRulePayload }) =>
      updateRule(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
    },
  });
}

export function useOverridePrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OverridePricePayload) => overridePrice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing'] });
    },
  });
}

export function useAuditLog(query?: AuditLogQuery) {
  return useQuery({
    queryKey: ['pricing', 'audit', query],
    queryFn: () => fetchAuditLog(query),
    staleTime: 30_000,
  });
}
